import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom';
import { handleBeforeInput } from '../RichEditor/utils';
import { Editor, convertToRaw } from 'draft-js';
import { transformTextToEditorState } from '../../utils/dataSync';
import { useStore } from '../../store/store';
import { debounce } from 'lodash';
import { convertPadding, getHeaderStyle } from '../../utils/table';
import { useStyleMap } from '../../hooks';
import EditorWrapper from '../common/EditorWrapper';
import ContextualMenu from '../ContextualMenu/ContextualMenu';
import FloatyTextStyling from './FloatyTextStyling';
import { useCellClickOutside } from './useCellClickOutside';
import { useInsertionTargetRegistration } from './insertionTarget';
import { MENU_ANCESTORS } from '../../constants/constants';
import { EditorProvider } from '../../contexts/EditorContext';
import { useTabHandler } from '../../hooks';
import { RichUtils } from 'draft-js';
import '../RichEditor/index.css'
import { useStyledPaste } from '../RichEditor/useStyledPaste';
import { mergeConfigurationHighlightColors } from '../../utils/styles';

const ParentHeader = ({
  rowSpan,
  colSpan,
  columnKey,
  title,
  isLastColumn,
  tableStyle,
  configuration,
  boxId,
  tableIsSelected,
  align = 'center',
  updateTheadHeight
}) => {
  const styleMap = useStyleMap()
  const setEventManagerEnabled = useStore(({ setEventManagerEnabled }) => setEventManagerEnabled)
  const updateBox = useStore(({ updateBox }) => updateBox)
  const selectedBoxId = useStore(({ selectedBoxId }) => selectedBoxId)

  const [editorState, setEditorState] = useState(transformTextToEditorState(title));
  const [isSelected, setIsSelected] = useState(false)
  useInsertionTargetRegistration({
    boxId,
    isSelected: tableIsSelected && isSelected,
    editorState,
    setEditorState,
    hasImage: false,
  });

  useEffect(() => {
    if (!isSelected) {
      setEditorState(transformTextToEditorState(title))
    }
  }, [title, isSelected]);

  const inputRef = useRef()
  const ref = useRef()

  const style = getHeaderStyle({ tableStyle, configuration, isLastColumn })

  const focus = useCallback(() => {
    setIsSelected(true)
    setEventManagerEnabled(false);
    inputRef.current.focus()
  }, [setEventManagerEnabled])

  const handleFocus = (e) => {
    if (tableIsSelected && !isSelected) {
      setIsSelected(true)
      e.preventDefault();
      focus()
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const save = useCallback(debounce((newEditorState) => {
    updateTheadHeight();
    updateBox(boxId, (box) => {
      // Sauvegarder dans columnsDisposition
      const updateColumnTitle = (columns) => {
        return columns.map(col => {
          if (col.key === columnKey) {
            return {
              ...col,
              title: JSON.stringify(convertToRaw(newEditorState.getCurrentContent()))
            };
          }
          if (col.children) {
            return {
              ...col,
              children: updateColumnTitle(col.children)
            };
          }
          return col;
        });
      };

      if (box.content.columnsDisposition) {
        box.content.columnsDisposition = updateColumnTitle(box.content.columnsDisposition);
      }
    });
  }, 100), [updateTheadHeight, boxId, columnKey]);

  const { myKeyBindingFn } = useTabHandler(editorState, setEditorState);

  const handleKeyCommand = useCallback(
    (command) => {
      if (command) {
        setEditorState(
          RichUtils.toggleInlineStyle(editorState, command.toUpperCase())
        );
      }
    },
    [editorState]
  );

  useEffect(() => {
    if (tableIsSelected && isSelected) {
      save(editorState);
    }
  }, [editorState, save, tableIsSelected, isSelected]);

  const { padding, minHeight } = convertPadding(tableStyle.size, tableStyle.customCellPadding, tableStyle.customCellPaddingUnit);

  useCellClickOutside({
    ref,
    isSelected,
    onClickOutside: () => {
      save(editorState)
      setIsSelected(false)
    },
  });

  // Collage mis en forme, ou texte seul (raccourci, pastille).
  const highlightColors = useMemo(() => mergeConfigurationHighlightColors(configuration), [configuration]);
  const styledPaste = useStyledPaste({
    editorState,
    write: setEditorState,
    anchorRef: ref,
    active: tableIsSelected && isSelected,
    colors: configuration.colors,
    highlightColors,
    defaultColor: tableStyle.customHeaderTextColor,
    defaultFontSize: style?.fontSize,
  });

  const menuAncestorIds = useMemo(() => [boxId, columnKey], [boxId, columnKey])

  return (
    <th
      scope="col"
      rowSpan={rowSpan}
      colSpan={colSpan}
      style={style}
      onClick={(e) => {
        handleFocus(e)
      }}
      ref={ref}
    >
      <div style={{
        margin: 0,
        backgroundColor: 'transparent',
        zIndex: tableIsSelected ? 1000 : 'auto',
        border: 'none',
        color: style.color,
        padding,
        fontSize: style?.fontSize,
        fontFamily: style?.fontFamily,
        fontWeight: 'normal',
        minHeight,
        height: '100%',
        lineHeight: 1.5714285714285714,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
      }}>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: align === 'left' ? 'flex-start' : align === 'center' ? 'center' : align === 'right' ? 'flex-end' : align === 'justify' ? 'flex-start' : 'center',
          minHeight: 0,
          wordWrap: 'break-word',
          wordBreak: 'normal'
        }}>
          <div
            style={{
              width: '100%',
              overflow: 'hidden'
            }}
            onKeyDown={styledPaste.onKeyDown}
            onKeyUp={styledPaste.onKeyUp}
          >
            <EditorProvider setEditorState={setEditorState} editorState={editorState} readOnly={!isSelected}>
              <EditorWrapper
                boxId={`${boxId}-${columnKey}}`}
                editorState={editorState}
                box={{ id: `${boxId}-${columnKey}}`, content: { alignment: align } }}
                textStyle={{ positions: { horizontal: align } }}
              >
                <Editor
                  stripPastedStyles={true}
                  handlePastedText={styledPaste.handlePastedText}
                  handleBeforeInput={(chars, newEditorState) => handleBeforeInput(
                    {
                      chars,
                      editorState: newEditorState,
                      setEditorState
                    }
                  )}
                  editorState={editorState}
                  ref={inputRef}
                  onChange={!boxId ? null : ((e) => setEditorState(e))}
                  textAlignment={align === 'justify' ? 'left' : align}
                  readOnly={!isSelected}
                  customStyleMap={styleMap}
                  keyBindingFn={myKeyBindingFn}
                  handleKeyCommand={handleKeyCommand}
                  onBlur={() => {
                    save(editorState);
                    updateBox(boxId, (box) => {
                      box.selectedItem = undefined
                    }
                    );
                  }}
                />
              </EditorWrapper>
            </EditorProvider>
          </div>
        </div>
      </div>
      {tableIsSelected &&
        ReactDOM.createPortal(
          <ContextualMenu
            editorState={editorState}
            setEditorState={setEditorState}
            menuAncestorType={MENU_ANCESTORS.BOX.header}
            menuAncestorIds={menuAncestorIds}
            hideImage
          />,
          document.getElementById('SelectionVariablePortal')
        )}
      {styledPaste.pasteChoice}
      {tableIsSelected && isSelected &&
        ReactDOM.createPortal(
          <FloatyTextStyling
            editorState={editorState}
            setEditorState={setEditorState}
            defaultFontSize={style?.fontSize}
            defaultFontFamily={style?.fontFamily}
            defaultColor={tableStyle.customHeaderTextColor}
            colors={configuration.colors}
          />,
          document.getElementById('SelectionVariablePortal')
        )}
    </th>
  )
}

export default ParentHeader
